import { BonusIcon, describePrints } from "@boundary/components/bonusIcons/bonusIcons";
import styles from "./payWith.module.scss";

interface CartCalculation {
  freePrintsUsed: Record<string, number>;
  freePrintsAvailable: Record<string, number>;
  freePrintsDiscount: number;
  pointsUsed: number;
  pointsAvailable: number;
  minCardPayment: number;
}

interface PayWithProps {
  calculation: CartCalculation | null;
  payWith: { freePrints: boolean; points: boolean };
  onChange: (next: Partial<{ freePrints: boolean; points: boolean }>) => void;
  disabled?: boolean;
}

function money(value: number): string {
  return `${Math.round(value).toLocaleString("ru-RU")} ₽`;
}

/**
 * Что именно покроют бесплатные отпечатки в этом заказе.
 *
 * Ключ ресурса — `услуга` или `услуга|цвет:формат`; для подписи нужна только
 * услуга, разновидность человек и так видит в бонусах.
 */
function printsSummary(used: Record<string, number>): string {
  return Object.entries(used)
    .filter(([, count]) => count > 0)
    .map(([resource, count]) => describePrints(resource.split("|")[0], count))
    .join(", ");
}

/**
 * Чем платить за заказ.
 *
 * Показывается, только когда есть чем: пустой блок с двумя выключенными
 * переключателями лишь занимает место и наводит на мысль, что что-то не работает.
 *
 * Награды включены по умолчанию — человек их заработал. Выключатель нужен для
 * обратного случая: приберечь бесплатный полароид до нужного снимка.
 */
export function PayWith({ calculation, payWith, onChange, disabled }: PayWithProps) {
  if (!calculation) {
    return null;
  }

  const hasPrints = Object.values(calculation.freePrintsAvailable).some((n) => n > 0);
  const hasPoints = calculation.pointsAvailable > 0;
  if (!hasPrints && !hasPoints) {
    return null;
  }

  const printsCovered = printsSummary(calculation.freePrintsUsed);
  const printsUnused =
    payWith.freePrints && hasPrints && !printsCovered
      ? "В этом заказе нечего покрыть"
      : null;

  return (
    <div className={styles.block}>
      <p className={styles.title}>Чем платим</p>

      {hasPrints ? (
        <label className={styles.row}>
          <BonusIcon kind="coupon" className={styles.icon} />
          <span className={styles.body}>
            <span className={styles.name}>Купоны</span>
            <span className={styles.meta}>
              {printsCovered
                ? `Покроет ${printsCovered} · −${money(calculation.freePrintsDiscount)}`
                : printsUnused || "Есть в бонусах"}
            </span>
          </span>
          <input
            type="checkbox"
            className={styles.toggle}
            checked={payWith.freePrints}
            disabled={disabled}
            onChange={(e) => onChange({ freePrints: e.target.checked })}
          />
        </label>
      ) : null}

      {hasPoints ? (
        <label className={styles.row}>
          <BonusIcon kind="points" className={styles.icon} />
          <span className={styles.body}>
            <span className={styles.name}>Бонусы</span>
            <span className={styles.meta}>
              {calculation.pointsUsed > 0
                ? `Спишем ${money(calculation.pointsUsed)} из ${money(calculation.pointsAvailable)}`
                : `Доступно ${money(calculation.pointsAvailable)}`}
            </span>
          </span>
          <input
            type="checkbox"
            className={styles.toggle}
            checked={payWith.points}
            disabled={disabled}
            onChange={(e) => onChange({ points: e.target.checked })}
          />
        </label>
      ) : null}

      <p className={styles.note}>
        {money(calculation.minCardPayment)} оплачивается картой в любом случае —
        полностью бонусами заказ провести нельзя.
      </p>
    </div>
  );
}
